import { useEffect } from 'react';
import { LoadingState, ErrorState } from '../../components/LoadingState';
import { MermaidBlock } from '../../components/MermaidBlock';
import { useGenerateRemember } from '../../api/queries';
import styles from './RememberView.module.css';

// Response shape from /activity/remember as observed in the legacy widget.
type Remember = {
  central_theme?: string;
  key_concepts?: string[];
  formula_or_rules?: string[];
  visual_anchors?: Array<{ concept?: string; image?: string }>;
  // connections_map is a mermaid graph string. We don't render it as a
  // diagram for now (would duplicate the Mermaid pipeline used by the
  // Diagram feature) — show it as a collapsible code block instead.
  connections_map?: string;
  memory_aids?: Array<{ technique?: string; application?: string }>;
  remember_tips?: string[];
};

export function RememberView() {
  const mut = useGenerateRemember();
  useEffect(() => {
    if (!mut.data && !mut.isPending && !mut.error) mut.mutate({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (mut.isPending) return <LoadingState label="Construyendo el repaso…" hint="Destilando lo esencial para recordar" />;
  if (mut.error) return <ErrorState error={mut.error} onRetry={() => mut.mutate({})} />;
  const data = mut.data as any;
  if (!data) return null;

  const r: Remember = data.content?.remember_points ?? data.content ?? {};
  const title: string = data.title ?? data.content?.title ?? '';

  return (
    <div className={styles.view}>
      {title && <h2 className={styles.title}>{title}</h2>}

      {r.central_theme && (
        <section className={styles.themeBox}>
          <span className={styles.themeLabel}>Idea central</span>
          <p>{r.central_theme}</p>
        </section>
      )}

      {Array.isArray(r.key_concepts) && r.key_concepts.length > 0 && (
        <Section title="Conceptos clave">
          <ul className={styles.chips}>
            {r.key_concepts.map((c, i) => (
              <li key={i} className={styles.chip}>
                {c}
              </li>
            ))}
          </ul>
        </Section>
      )}

      {Array.isArray(r.formula_or_rules) && r.formula_or_rules.length > 0 && (
        <Section title="Reglas y fórmulas">
          <ul className={styles.bullets}>
            {r.formula_or_rules.map((f, i) => (
              <li key={i}>{f}</li>
            ))}
          </ul>
        </Section>
      )}

      {Array.isArray(r.visual_anchors) && r.visual_anchors.length > 0 && (
        <Section title="Anclajes visuales">
          <div className={styles.cards}>
            {r.visual_anchors.map((a, i) => (
              <article key={i} className={styles.card}>
                <h4>{a.concept}</h4>
                {a.image && <p>{a.image}</p>}
              </article>
            ))}
          </div>
        </Section>
      )}

      {Array.isArray(r.memory_aids) && r.memory_aids.length > 0 && (
        <Section title="Trucos para recordar">
          <div className={styles.cards}>
            {r.memory_aids.map((a, i) => (
              <article key={i} className={styles.card}>
                <h4>{a.technique}</h4>
                {a.application && <p>{a.application}</p>}
              </article>
            ))}
          </div>
        </Section>
      )}

      {Array.isArray(r.remember_tips) && r.remember_tips.length > 0 && (
        <Section title="Consejos">
          <ul className={styles.bullets}>
            {r.remember_tips.map((t, i) => (
              <li key={i}>{t}</li>
            ))}
          </ul>
        </Section>
      )}

      {r.connections_map && (
        <Section title="Mapa de conexiones">
          <div className={styles.mermaid}>
            <MermaidBlock code={r.connections_map} />
          </div>
        </Section>
      )}
    </div>
  );
}

function Section({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className={styles.section}>
      <h3 className={styles.sectionLabel}>{title}</h3>
      {children}
    </section>
  );
}
